import { millisecondsPer, toDate, toMilliseconds } from '@art-suite/art-core-ts';

import type { ApiScanNote } from '@kaiizen/kai-pro-api';
import type { ProgressUpdate, WorkStatus } from '@kaiizen/kai-scan-template-system';

/**
 * How long after a rework its Instruction Note may be written. The progress-update modal saves the
 * note in the same submit, moments after the status; a wider window, or any note from before the
 * change, starts picking up unrelated instructions — "Add Instructions" writes the same note type
 * for any status.
 */
const REWORK_REASON_WINDOW_MS = millisecondsPer.minute * 10;

/** One work-status change in a task's activity log. */
export type TaskWorkProgressRow = {
  id: string;
  at: Date;
  actorUserId?: string;
  status: WorkStatus;
  previousStatus?: WorkStatus;
  reason?: string;
};

type StatusChange = Omit<TaskWorkProgressRow, 'id' | 'reason'>;

const sortByUpdatedAt = (progressUpdates: readonly ProgressUpdate[]) =>
  [...progressUpdates].sort((a, b) => toMilliseconds(a.updatedAt) - toMilliseconds(b.updatedAt));

/** Construction tasks start at Not Started, so a first change comes from there — unless it is Not Started itself. */
const getStartingStatus = (status: WorkStatus): WorkStatus | undefined => status === 'notStarted' ? undefined : 'notStarted';

const getStatusChanges = (progressUpdates: readonly ProgressUpdate[]): StatusChange[] => {
  const changes: StatusChange[] = [];
  let previousStatus: WorkStatus | undefined;
  for (const { status, updatedAt, updatedByUserId } of sortByUpdatedAt(progressUpdates)) {
    if (!status || status === previousStatus) continue;
    changes.push({ at: toDate(updatedAt), actorUserId: updatedByUserId, status, previousStatus: previousStatus ?? getStartingStatus(status) });
    previousStatus = status;
  }
  return changes;
};

const getMsAfterChange = (change: StatusChange, note: ApiScanNote) => toMilliseconds(note.createdAt) - change.at.getTime();

const isReasonCandidate = (change: StatusChange, note: ApiScanNote) =>
  note.scanNoteType === 'instructions'
  && note.userId === change.actorUserId
  && !!note.body.trim()
  && getMsAfterChange(change, note) >= 0
  && getMsAfterChange(change, note) <= REWORK_REASON_WINDOW_MS;

const getReasonCandidates = (changes: readonly StatusChange[], scanNotes: readonly ApiScanNote[]) =>
  changes
    .flatMap((change, changeIndex) =>
      change.status === 'needsRework'
        ? scanNotes.filter(note => isReasonCandidate(change, note)).map(note => ({ changeIndex, note, msAfterChange: getMsAfterChange(change, note) }))
        : []
    )
    .sort((a, b) => a.msAfterChange - b.msAfterChange);

/**
 * Each rework's reason, keyed by its change's index. Rework–note pairs are claimed closest first,
 * and each note and each rework is used at most once — so a rework takes the soonest note written
 * after it that no closer pairing has already taken.
 */
const getReworkReasons = (changes: readonly StatusChange[], scanNotes: readonly ApiScanNote[]) => {
  const reasons = new Map<number, string>();
  const claimedNoteIds = new Set<string>();
  for (const { changeIndex, note } of getReasonCandidates(changes, scanNotes)) {
    if (reasons.has(changeIndex) || claimedNoteIds.has(note.id)) continue;
    claimedNoteIds.add(note.id);
    reasons.set(changeIndex, note.body.trim());
  }
  return reasons;
};

/**
 * A task's work-status history as activity-log rows, newest first: one row per real status
 * change, each carrying the status before it, and — for a rework — the body of the Instruction
 * Note its author wrote within ten minutes after it.
 */
export const getTaskWorkProgressRows = ({ progressUpdates = [], scanNotes = [] }: {
  progressUpdates?: readonly ProgressUpdate[];
  scanNotes?: readonly ApiScanNote[];
}): TaskWorkProgressRow[] => {
  const changes = getStatusChanges(progressUpdates);
  const reasons = getReworkReasons(changes, scanNotes);
  return changes
    .map((change, index) => ({ ...change, id: `work-progress-${index}`, reason: reasons.get(index) || undefined }))
    .reverse();
};

/** The design's status wording for the activity log — deliberately not the badge's ("Rework", "Completed"). */
export const WORK_PROGRESS_STATUS_LABEL: Record<WorkStatus, string> = {
  notStarted: 'Not Started',
  inProgress: 'In Progress',
  needsRework: 'Needs Rework',
  completed: 'Complete',
};

/** A row's headline, e.g. "Marked In Progress". */
export const getTaskWorkProgressName = ({ status }: TaskWorkProgressRow) => `Marked ${WORK_PROGRESS_STATUS_LABEL[status]}`;

/** A row's second line: the rework reason when there is one, otherwise the change it made. */
export const getTaskWorkProgressDetail = ({ status, previousStatus, reason }: TaskWorkProgressRow): string | undefined =>
  reason ?? (previousStatus ? `${WORK_PROGRESS_STATUS_LABEL[previousStatus]} → ${WORK_PROGRESS_STATUS_LABEL[status]}` : undefined);
